<template>
  <div 
    class="featured-carousel-container mb-3" 
    ref="container"
    :class="[spaceLevel, currentAdType, { 'hidden': !isVisible }]"
    :data-space-level="spaceLevel"
    :data-image-type="currentAdType"
  >
    <b-card v-if="workingAds.length > 0 && isVisible" no-body class="bg-transparent dc-info-panel">
      <!-- Mixed Carousel for All Ad Types -->
      <b-carousel
        id="mixed-ads-carousel"
        ref="adsCarousel"
        v-model="currentAdIndex"
        :interval="carouselInterval"
        controls
        indicators
        background="#ffffff00"
        class="mixed-featured-carousel"
        @slide="onSlideChange"
        @sliding-start="onSlidingStart"
        @sliding-end="onSlidingEnd"
      >
        <b-carousel-slide
          v-for="(ad, index) in workingAds"
          :key="ad.val + '-' + index"
          @click.native="openAd(ad)"
        >
          <template #img>
            <div class="featured-slide" :class="spaceLevel">
              <!-- Dynamic Ad Component Based on Type -->
              <ImageAd
                v-if="(ad._firebase && ad._firebase.type === 'image') || !ad._firebase || !ad._firebase.type"
                :ad="ad"
                :spaceLevel="spaceLevel"
                @imageLoaded="onContentLoad"
                @imageError="onContentError"
              />
              
              <VideoAd
                v-else-if="ad._firebase && ad._firebase.type === 'video'"
                :ad="ad"
                :spaceLevel="spaceLevel"
                @videoLoaded="onContentLoad"
                @videoError="onContentError"
                @videoPlay="onVideoPlay"
                @videoEnded="onVideoEnded"
              />
              
              <PollAd
                v-else-if="ad._firebase && ad._firebase.type === 'poll'"
                :ad="ad"
                :spaceLevel="spaceLevel"
                @pollLoaded="onContentLoad"
              />
              
              <TestimonialAd
                v-else-if="ad._firebase && ad._firebase.type === 'testimonial'"
                :ad="ad"
                :spaceLevel="spaceLevel"
                @testimonialLoaded="onContentLoad"
              />
              
              <InformationAd
                v-else-if="ad._firebase && ad._firebase.type === 'information'"
                :ad="ad"
                :spaceLevel="spaceLevel"
                @informationLoaded="onContentLoad"
              />
            </div>
          </template>
        </b-carousel-slide>
      </b-carousel>
    </b-card>
  </div>
</template>

<script>
// cspell:ignore vuex
import { mapGetters } from "vuex";
import { useAdTracker, adContextFromRoute } from "@/composables/useAdTracker";
import ImageAd from "./ads/ImageAd.vue";
import VideoAd from "./ads/VideoAd.vue";
import PollAd from "./ads/PollAd.vue";
import TestimonialAd from "./ads/TestimonialAd.vue";
import InformationAd from "./ads/InformationAd.vue";

export default {
  name: "AdvertiseCard",
  components: {
    ImageAd,
    VideoAd,
    PollAd,
    TestimonialAd,
    InformationAd
  },
  data() {
    return {
      currentAdIndex: 0,
      MIN_HEIGHT: 150,
      PREFERRED_MIN: 200,
      HIDE_THRESHOLD: 100,
      currentImageAspectRatio: 1,
      currentAdType: 'square',
      isVisible: true,
      spaceLevel: 'normal',
      // Performance optimization caches
      shuffledAds: [],
      formattedAdsCache: null,
      lastAdListHash: null,
      // Debouncing
      resizeDebounceTimer: null,
      // Failed ad tracking
      failedAdUrls: new Set(),
      isUpdating: false,
      // Video playback control
      isVideoPlaying: false,
      defaultInterval: 15000,
      // Store initial container position to prevent cumulative height calculation errors
      initialTop: null,
      // Engagement tracker: counts viewable renders and clicks, flushed in
      // batches on tab-hide. Shared across the tab, so it is held rather than
      // created here.
      adTracker: useAdTracker()
    };
  },
  computed: {
    ...mapGetters({
      getAds: "moduleMaster/getAds",
    }),
    adList() {
      return this.getCachedAdList();
    },
    allAds() {
      return this.getCachedShuffledAds();
    },
    workingAds() {
      return this.allAds.filter(ad => !this.failedAdUrls.has(ad.url));
    },
    currentAd() {
      return this.workingAds[this.currentAdIndex] || null;
    },
    carouselInterval() {
      // Always return default interval - we'll use pause/cycle methods instead
      return this.defaultInterval;
    }
  },
  methods: {
    pauseAllVideos() {
      // Find and pause all video elements EXCEPT the currently active one
      try {
        // Get the currently active carousel slide
        const activeSlide = this.$el?.querySelector('.carousel-item.active');
        let activeVideo = null;
        
        if (activeSlide) {
          activeVideo = activeSlide.querySelector('video');
        }
        
        // Method 1: Search in current element
        if (this.$el) {
          const videos = this.$el.querySelectorAll('video');
          videos.forEach(video => {
            // Only pause if this is NOT the currently active video
            if (video !== activeVideo) {
              if (!video.paused) {
                video.pause();
              }
              video.muted = true;
              // Only reset time if video has actually played
              if (video.currentTime > 0) {
                video.currentTime = 0;
              }
            }
          });
        }
        
        // Method 2: Search globally for video elements (backup)
        const allVideos = document.querySelectorAll('video');
        allVideos.forEach(video => {
          // Only pause videos that are in ad components AND not the active one
          if ((video.closest('.featured-carousel-container') || video.closest('.video-feature-container')) && video !== activeVideo) {
            if (!video.paused) {
              video.pause();
            }
            video.muted = true;
            // Only reset time if video has actually played
            if (video.currentTime > 0) {
              video.currentTime = 0;
            }
          }
        });
      } catch (error) {
        // Error pausing videos
      }
    },

    pauseAllVideosCompletely() {
      // Pause ALL videos without exception - used when manually changing slides
      try {
        let pausedCount = 0;
        
        // Method 1: Search in current element
        if (this.$el) {
          const videos = this.$el.querySelectorAll('video');
          videos.forEach((video, index) => {
            if (!video.paused) {
              video.pause();
              pausedCount++;
            }
            video.muted = true;
            // Only reset time if video has actually played (duration > 0 and currentTime > 0)
            if (video.currentTime > 0) {
              video.currentTime = 0;
            }
          });
        }
        
        // Method 2: Search globally for video elements (backup)
        const allVideos = document.querySelectorAll('video');
        allVideos.forEach((video, index) => {
          // Pause ALL videos that are in ad components
          const isInAdsContainer = video.closest('.featured-carousel-container') || video.closest('.video-feature-container') || video.closest('[class*="video"]') || video.closest('[class*="featured"]');
          if (isInAdsContainer) {
            if (!video.paused) {
              video.pause();
              pausedCount++;
            }
            video.muted = true;
            // Only reset time if video has actually played
            if (video.currentTime > 0) {
              video.currentTime = 0;
            }
          }
        });
        
      } catch (error) {
        // Error completely pausing videos
      }
    },

    tryStartCurrentVideo() {
      // Try to start the currently active video if it's a video ad
      try {
        const activeSlide = this.$el?.querySelector('.carousel-item.active');
        if (activeSlide) {
          const activeVideo = activeSlide.querySelector('video');
          if (activeVideo && this.currentAd?._firebase?.type === 'video') {
            // Reset video to beginning and ensure it's muted for autoplay
            activeVideo.currentTime = 0;
            activeVideo.muted = true;
            
            // Attempt to play the video
            const playPromise = activeVideo.play();
            if (playPromise !== undefined) {
              playPromise.then(() => {
                // Video started playing successfully
              }).catch(error => {
                // Failed to start video
              });
            }
          }
        }
      } catch (error) {
        // Error trying to start current video
      }
    },

    attachCarouselControlListeners() {
      // Attach direct click listeners to carousel controls for immediate video pausing
      try {
        if (this.$el) {
          const prevControl = this.$el.querySelector('.carousel-control-prev');
          const nextControl = this.$el.querySelector('.carousel-control-next');
          const indicators = this.$el.querySelectorAll('.carousel-indicators button');

          if (prevControl) {
            prevControl.addEventListener('click', () => {
              this.pauseAllVideosCompletely();
            });
          }

          if (nextControl) {
            nextControl.addEventListener('click', () => {
              this.pauseAllVideosCompletely();
            });
          }

          indicators.forEach(indicator => {
            indicator.addEventListener('click', () => {
              this.pauseAllVideosCompletely();
            });
          });
        }
      } catch (error) {
        // Error attaching carousel control listeners
      }
    },
    
    openInNewTab(url) {
      window.open(url, "_blank", "noreferrer");
    },

    /**
     * A click on a carousel slide: record it, then follow the link.
     *
     * Recording first because the tab may navigate away immediately; the
     * tracker only touches memory and localStorage here, so this costs nothing.
     */
    openAd(ad) {
      if (!ad) return;
      this.adTracker.trackClick(ad.val, "carousel", ad.link);
      if (ad.link) this.openInNewTab(ad.link);
    },

    /**
     * Point the viewability observer at whichever slide is currently showing.
     *
     * The carousel reuses slide elements as it cycles, so the observer is
     * re-registered on every settle rather than once per ad.
     */
    observeCurrentSlide() {
      const activeSlide = this.$el?.querySelector(".carousel-item.active");
      if (!activeSlide || !this.currentAd?.val) return;
      this.adTracker.observe(activeSlide, this.currentAd.val, "carousel");
    },
    formatAdList(list) {
      if (list?.length < 1) return [];
      return list
        .filter(i => i.desc || (i._firebase && this.hasValidContent(i._firebase)))
        .map(i => this.formatAdByType(i));
    },
    
    hasValidContent(firebaseData) {
      const type = firebaseData.type || 'image';
      switch (type) {
        case 'image':
          return !!firebaseData.imageUrl;
        case 'video':
          return !!firebaseData.videoUrl;
        case 'poll':
          return !!firebaseData.question && firebaseData.options?.length > 0;
        case 'testimonial':
          return !!firebaseData.reviewText && !!firebaseData.patientName;
        case 'information':
          return !!firebaseData.title && !!firebaseData.message;
        default:
          return false;
      }
    },
    
    formatAdByType(ad) {
      const type = ad._firebase?.type || 'image';
      const baseAd = { ...ad, url: ad.desc, link: ad.label };
      
      switch (type) {
        case 'video':
          return {
            ...baseAd,
            url: ad._firebase?.videoUrl || ad.desc,
            poster: ad._firebase?.thumbnailUrl,
            title: ad._firebase?.title,
            autoplay: ad._firebase?.autoplay,
            muted: ad._firebase?.muted
          };
        case 'poll':
          return {
            ...baseAd,
            question: ad._firebase?.question,
            options: ad._firebase?.options || [],
            showResults: ad._firebase?.showResults || false,
            backgroundUrl: ad._firebase?.backgroundUrl,
            title: ad._firebase?.title
          };
        case 'testimonial':
          return {
            ...baseAd,
            testimonial: ad._firebase?.reviewText,
            patientName: ad._firebase?.patientName,
            patientPhoto: ad._firebase?.patientPhoto,
            rating: ad._firebase?.rating || 5,
            title: ad._firebase?.title
          };
        case 'information':
          return {
            ...baseAd,
            title: ad._firebase?.title,
            message: ad._firebase?.message,
            category: ad._firebase?.category,
            icon: ad._firebase?.icon,
            date: ad._firebase?.date,
            linkUrl: ad._firebase?.linkUrl
          };
        default: // image
          return baseAd;
      }
    },
    shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },
    updateContainerSize() {
      // Debounce resize operations to prevent excessive DOM updates
      if (this.resizeDebounceTimer) {
        clearTimeout(this.resizeDebounceTimer);
      }
      
      this.resizeDebounceTimer = setTimeout(() => {
        this.performContainerSizeUpdate();
      }, 16); // ~60fps
    },
    
    performContainerSizeUpdate() {
      if (!this.$refs.container || this.isUpdating) return;
      this.isUpdating = true;

      // Use initial top position if available, otherwise get current position
      const containerRect = this.$refs.container.getBoundingClientRect();
      const topPosition = this.initialTop !== null ? this.initialTop : containerRect.top;
      const windowHeight = window.innerHeight;
      const availableHeight = windowHeight - topPosition - 20; // 20px buffer
      
      // Check responsive constraints based on screen size
      this.checkResponsiveConstraints();
      
      let finalHeight;
      let shouldShow = true;
      
      if (availableHeight < this.HIDE_THRESHOLD) {
        // Too little space - use default minimum height
        finalHeight = this.MIN_HEIGHT;
        this.spaceLevel = 'minimal';
      } else if (availableHeight < this.MIN_HEIGHT) {
        // Very limited space - use absolute minimum
        finalHeight = this.MIN_HEIGHT;
        this.spaceLevel = 'minimal';
      } else if (availableHeight < this.PREFERRED_MIN) {
        // Limited space - use available but it's small
        finalHeight = availableHeight;
        this.spaceLevel = 'limited';
      } else {
        // Good space - use available height
        finalHeight = availableHeight;
        this.spaceLevel = 'normal';
      }
      
      this.updateComponentVisibility(shouldShow);
      if (shouldShow) {
        this.updateContainerDimensions(finalHeight);
      }
      this.isUpdating = false;
    },
    
    updateComponentVisibility(shouldShow) {
      this.isVisible = shouldShow;
      const container = this.$refs.container;
      if (shouldShow) {
        container.style.display = 'block';
        container.classList.remove('hidden');
      } else {
        container.style.display = 'none';
        container.classList.add('hidden');
      }
    },
    
    updateContainerDimensions(height) {
      const container = this.$refs.container;
      const maxWidth = container.parentElement.clientWidth;
      
      // Get current ad type for mixed carousel
      const adType = this.currentAd?._firebase?.type || 'image';
      
      // Apply space level class and current ad type
      container.className = `featured-carousel-container mb-3 ${this.spaceLevel} ${this.currentAdType}`;
      if (!this.isVisible) {
        container.classList.add('hidden');
      }
      
      container.setAttribute('data-space-level', this.spaceLevel);
      container.setAttribute('data-image-type', this.currentAdType);
      container.setAttribute('data-ad-type', adType);
      
      // Ad type specific sizing logic
      if (adType === 'poll') {
        // Polls use dynamic height based on number of options to prevent cutoff
        const maxReasonableSize = 450;
        const optimalWidth = Math.min(maxWidth * 0.9, maxReasonableSize);
        
        // Calculate dynamic height based on poll content
        const numberOfOptions = this.currentAd?.options?.length || 4; // Default to 4 if not available
        const baseHeight = 200; // Base height for title + question + padding
        const optionHeight = 65; // Height per option (including margins)
        const footerHeight = 80; // Height for vote button + dots
        const calculatedHeight = baseHeight + (numberOfOptions * optionHeight) + footerHeight;
        
        // Apply reasonable min/max limits
        const minHeight = 350;
        const maxHeight = 650;
        const finalHeight = Math.min(Math.max(calculatedHeight, minHeight), maxHeight);
        
        container.style.setProperty('--container-width', `${optimalWidth}px`);
        container.style.setProperty('--container-height', `${finalHeight}px`);
        
      } else if (adType === 'testimonial') {
        // Testimonials use square proportions
        const maxReasonableSize = 450;
        const optimalWidth = Math.min(maxWidth * 0.9, maxReasonableSize);
        const squareHeight = optimalWidth; // Perfect square: height = width
        
        container.style.setProperty('--container-width', `${optimalWidth}px`);
        container.style.setProperty('--container-height', `${squareHeight}px`);
        
      // Information ads now use the same square sizing as testimonials (handled in fallback logic below)
      } else if (adType === 'video') {
        // Videos maintain their aspect ratio
        const aspectRatio = this.currentImageAspectRatio || (16/9); // Default to 16:9 for videos
        const widthFromHeight = height * aspectRatio;
        
        if (widthFromHeight <= maxWidth) {
          container.style.setProperty('--container-width', `${widthFromHeight}px`);
          container.style.setProperty('--container-height', `${height}px`);
        } else {
          const heightFromWidth = maxWidth / aspectRatio;
          container.style.setProperty('--container-width', `${maxWidth}px`);
          container.style.setProperty('--container-height', `${heightFromWidth}px`);
        }
        
      } else {
        // Images: use existing logic
        if (this.currentAdType === 'square') {
          const optimalWidth = Math.min(height, maxWidth);
          const finalHeight = optimalWidth;
          
          container.style.setProperty('--container-width', `${optimalWidth}px`);
          container.style.setProperty('--container-height', `${finalHeight}px`);
          
        } else {
          const aspectRatio = this.currentImageAspectRatio;
          const widthFromHeight = height * aspectRatio;
          
          if (widthFromHeight <= maxWidth) {
            container.style.setProperty('--container-width', `${widthFromHeight}px`);
            container.style.setProperty('--container-height', `${height}px`);
          } else {
            const heightFromWidth = maxWidth / aspectRatio;
            container.style.setProperty('--container-width', `${maxWidth}px`);
            container.style.setProperty('--container-height', `${heightFromWidth}px`);
          }
        }
      }
    },
    
    checkResponsiveConstraints() {
      const screenHeight = window.innerHeight;
      const isSmallScreen = screenHeight < 600;
      const isMediumScreen = screenHeight < 900;
      
      if (isSmallScreen) {
        // Very small screens: prioritize functionality over ads
        this.MIN_HEIGHT = 160;
        this.PREFERRED_MIN = 200;
        this.HIDE_THRESHOLD = 120;
      } else if (isMediumScreen) {
        // Medium screens: balanced approach
        this.MIN_HEIGHT = 220;
        this.PREFERRED_MIN = 280;
        this.HIDE_THRESHOLD = 140;
      } else {
        // Large screens: full experience
        this.MIN_HEIGHT = 280;
        this.PREFERRED_MIN = 350;
        this.HIDE_THRESHOLD = 180;
      }
    },
    
    onContentLoad(contentInfo) {
      this.currentImageAspectRatio = contentInfo.aspectRatio;
      this.currentAdType = contentInfo.type;
      
      this.updateContainerSize();
    },
    
    onContentError(contentInfo) {
      this.currentImageAspectRatio = contentInfo?.aspectRatio || 1;
      this.currentAdType = contentInfo?.type || 'square';
      
      // Add failed ad to the set to filter it out
      if (this.currentAd?.url) {
        this.failedAdUrls.add(this.currentAd.url);
      }
    },
    
    onSlidingStart() {
      // As soon as sliding starts, pause all videos completely
      this.pauseAllVideosCompletely();
      
      // Reset video playing state
      this.isVideoPlaying = false;
    },

    onSlidingEnd() {
      this.updateContainerForCurrentAd();

      // After slide ends, try to start the new video if it's a video ad
      this.$nextTick(() => {
        this.tryStartCurrentVideo();
        this.observeCurrentSlide();
      });
    },

    onSlideChange() {
      // This is a fallback - the main logic is now in sliding events
      this.pauseAllVideosCompletely();
      
      // Reset video playing state when slide changes and resume carousel
      const wasVideoPlaying = this.isVideoPlaying;
      this.isVideoPlaying = false;
      
      // Resume carousel auto-rotation if we were on a video before
      if (wasVideoPlaying && this.$refs.adsCarousel) {
        this.$refs.adsCarousel.cycle();
      }
      
      this.updateContainerForCurrentAd();
      
      // After slide change, try to start the new video if it's a video ad
      this.$nextTick(() => {
        this.tryStartCurrentVideo();
      });
    },
    
    onVideoPlay() {
      // When a video starts playing, pause all OTHER videos (not the current one)
      this.pauseAllVideos();
      
      // Video started playing, pause carousel auto-rotation
      this.isVideoPlaying = true;
      if (this.$refs.adsCarousel) {
        this.$refs.adsCarousel.pause();
      }
    },
    
    onVideoEnded() {
      // Video ended, auto-advance to next ad after brief delay
      this.isVideoPlaying = false;
      if (this.$refs.adsCarousel) {
        setTimeout(() => {
          this.$refs.adsCarousel.next();
        }, 1000); // 1 second delay for smooth UX
      }
    },
    
    updateContainerForCurrentAd() {
      if (!this.currentAd) return;
      
      const adType = this.currentAd._firebase?.type || 'image';
      
      // Update current ad type for sizing and styling
      this.currentAdType = this.getAdTypeClass(adType);
      
      // Update container data attributes
      this.updateContainerAttributes(adType);
      
      // Update container size based on ad type
      this.updateContainerSize();
    },
    
    getAdTypeClass(firebaseType) {
      // Map Firebase ad types to display classes
      switch (firebaseType) {
        case 'video':
          return 'landscape'; // Videos are typically landscape
        case 'poll':
          return 'square'; // Polls use square layout for better proportions
        case 'testimonial':
          return 'square'; // Testimonials work well as square
        case 'information':
          return 'square'; // Information cards use same square layout as testimonials
        case 'image':
        default:
          return 'square'; // Default for images, will be updated by onContentLoad
      }
    },
    
    updateContainerAttributes(adType) {
      if (!this.$refs.container) return;
      
      // Update data attributes for CSS styling
      this.$refs.container.setAttribute('data-ad-type', adType);
    },
    
    getCachedAdList() {
      const rawAds = this.getAds;
      const currentHash = JSON.stringify(rawAds);
      
      // Use cache if data hasn't changed
      if (this.formattedAdsCache && this.lastAdListHash === currentHash) {
        return this.formattedAdsCache;
      }
      
      // Format and cache the result
      this.formattedAdsCache = this.formatAdList(rawAds) || [];
      this.lastAdListHash = currentHash;
      
      return this.formattedAdsCache;
    },
    
    getCachedShuffledAds() {
      const currentAdList = this.getCachedAdList();
      
      // Only shuffle when adList actually changes
      if (this.shuffledAds.length !== currentAdList.length || 
          !this.shuffledAds.every((ad, index) => ad.val === currentAdList[index]?.val)) {
        this.shuffledAds = [...currentAdList];
        this.shuffleArray(this.shuffledAds);
      }
      return this.shuffledAds;
    },
    
  },
  watch: {
    currentAdIndex: {
      handler() {
        // Use debounced update to prevent excessive DOM operations
        this.$nextTick(() => {
          this.updateContainerForCurrentAd();
        });
      },
      immediate: true
    },
    getAds: {
      handler() {
        // Clear cache when source data changes
        this.formattedAdsCache = null;
        this.lastAdListHash = null;
        this.shuffledAds = [];
        
        // When ads change, update container for first ad
        this.$nextTick(() => {
          this.updateContainerForCurrentAd();
        });
      },
      deep: true,
      immediate: false
    }
  },
  async mounted() {
    window.addEventListener("resize", () => {
      // On window resize, recalculate initial top position as viewport may have changed
      if (this.$refs.container) {
        const rect = this.$refs.container.getBoundingClientRect();
        this.initialTop = rect.top;
      }
      this.updateContainerSize();
    });
    
    // Initialize container without fetching ads
    // Ads are already fetched by the store's fetchAllConfig action
    this.$nextTick(() => {
      // Store initial top position before any resizing
      if (this.$refs.container) {
        const rect = this.$refs.container.getBoundingClientRect();
        this.initialTop = rect.top;
      }
      
      this.updateContainerSize();
      this.updateContainerForCurrentAd(); // Initialize container for first ad
      
      // Try to start the initial video if it's a video ad
      setTimeout(() => {
        this.tryStartCurrentVideo();
        this.attachCarouselControlListeners();
        this.observeCurrentSlide();
      }, 500); // Small delay to ensure DOM is ready
    });

    // Which branch these events belong to. Everything else about the event —
    // who, which role, which day — the server derives from the session.
    this.adTracker.setContext(adContextFromRoute(this.$route));

    // For testing - add global method to clear cache
    if (process.env.NODE_ENV === 'development') {
      window.clearAdsCache = () => {
        this.$store.dispatch('moduleMaster/clearAdsCache');
      };
    }
  },
  beforeDestroy() {
    // Note: We're using an anonymous function in addEventListener, so we can't remove it specifically
    // This is acceptable since the component is being destroyed anyway
    // Clear debounce timer
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
    }
    // Pause all videos before component destruction
    this.pauseAllVideosCompletely();
    // Stop watching slides, and send whatever was counted while this dashboard
    // was on screen. The tracker itself outlives the component.
    this.adTracker.unobserveAll();
    this.adTracker.flush();
  },
};
</script>

<style scoped>
.featured-carousel-container {
  width: var(--container-width, 100%);
  height: var(--container-height, 200px);
  min-height: var(--min-height, 150px);
  max-height: 80vh;
  margin: 0 auto;
  transition: all 0.3s ease;
  overflow: hidden;
}

/* Information ads now use standard square sizing like testimonials */

/* Space level classes */
.featured-carousel-container.normal {
  min-height: 200px;
}

.featured-carousel-container.limited {
  min-height: 150px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.featured-carousel-container.minimal {
  min-height: 120px;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.featured-carousel-container.hidden {
  display: none !important;
  height: 0;
  min-height: 0;
}

/* Image type classes */
.featured-carousel-container.square {
  width: var(--container-width, 100%);
  max-width: 100%;
}

.featured-carousel-container.landscape {
  width: var(--container-width, 100%);
}

.featured-carousel-container.portrait {
  width: var(--container-width, 100%);
}

.featured-carousel-container.information {
  width: var(--container-width, 100%);
}

.dc-info-panel {
  height: 100%;
}

.transparent-carousel,
.mixed-featured-carousel {
  height: 100%;
}

.featured-slide {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mixed-featured-carousel ::v-deep .carousel-inner {
  background-color: transparent !important;
  height: 100%;
}

.mixed-featured-carousel ::v-deep .carousel-item {
  height: 100%;
}

.mixed-featured-carousel ::v-deep .carousel-control-prev,
.mixed-featured-carousel ::v-deep .carousel-control-next {
  width: 40px;
  opacity: 0.8;
}

/* Information ads: Keep carousel structure like other ads with proper overflow containment */
.featured-carousel-container[data-ad-type="information"] .mixed-featured-carousel {
  height: 100%;
  overflow: hidden;
}

.featured-carousel-container[data-ad-type="information"] .mixed-featured-carousel ::v-deep .carousel-inner {
  height: 100%;
  overflow: hidden;
}

.featured-carousel-container[data-ad-type="information"] .mixed-featured-carousel ::v-deep .carousel-item {
  height: 100%;
  overflow: hidden;
}

.featured-carousel-container[data-ad-type="information"] .featured-slide {
  height: 100%;
  overflow: hidden;
}

/* Responsive mixed carousel controls */
.featured-carousel-container.minimal ::v-deep .mixed-featured-carousel .carousel-indicators {
  display: none;
}

.featured-carousel-container.minimal ::v-deep .mixed-featured-carousel .carousel-control-prev,
.featured-carousel-container.minimal ::v-deep .mixed-featured-carousel .carousel-control-next {
  width: 30px;
  opacity: 0.7;
}

.featured-carousel-container.limited ::v-deep .mixed-featured-carousel .carousel-control-prev,
.featured-carousel-container.limited ::v-deep .mixed-featured-carousel .carousel-control-next {
  width: 35px;
}

/* Ad type specific styling */
.featured-carousel-container[data-ad-type="video"] {
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.featured-carousel-container[data-ad-type="poll"] {
  background: transparent;
  border-radius: 12px;
  overflow: hidden;
}

.featured-carousel-container[data-ad-type="testimonial"] {
  background: transparent;
  border-radius: 12px;
  overflow: hidden;
}

.featured-carousel-container[data-ad-type="image"] {
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
}

/* Information ads removed - now use standard square sizing */

/* Responsive adjustments for small containers */
.featured-carousel-container[data-space-level="minimal"] .carousel-indicators {
  display: none;
}

.featured-carousel-container[data-space-level="minimal"] .carousel-control-prev,
.featured-carousel-container[data-space-level="minimal"] .carousel-control-next {
  width: 30px;
  opacity: 0.7;
}

.featured-carousel-container[data-space-level="limited"] .carousel-control-prev,
.featured-carousel-container[data-space-level="limited"] .carousel-control-next {
  width: 35px;
}

/* Emergency fallback for very small screens */
@media (max-height: 500px) {
  .featured-carousel-container {
    min-height: 100px !important;
    max-height: 120px !important;
  }
  
  .featured-carousel-container .carousel-indicators {
    display: none !important;
  }
}

@media (max-width: 768px) {
  .featured-carousel-container {
    max-width: 100%;
  }
  
  .featured-carousel-container.square {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
  }
}
</style>